/**
 * Client-side image compression for mobile uploads.
 *
 * Why this exists:
 *   Android Chrome users on Samsung One UI / Pixel / Xiaomi MIUI cameras
 *   commonly upload 12-30MB HEIC/JPEG files. On 4G/3G this hits the axios
 *   60s timeout AND the Kubernetes / Cloudflare ingress body size limits.
 *   By resizing+re-encoding in the browser we cut the payload to ~200KB-
 *   1MB while preserving enough fidelity for the AI pipeline (which
 *   downsamples to 2048px anyway).
 *
 * Strategy:
 *   - HEIC files: cannot decode in pure browser (would need libheif-js).
 *     We pass them through UNCHANGED — the backend has pillow_heif registered
 *     and handles them. The user still benefits from the timeout bump.
 *   - JPEG/PNG/WebP/BMP/JFIF: decode with createImageBitmap (auto-applies
 *     EXIF orientation in Chromium/WebKit) → draw on canvas at max 1280px
 *     long side → re-encode as JPEG q80.
 *   - If anything fails, fall back to the original file (backend will retry).
 *
 * Adaptive compression (iter 84, 09.06.2026 — production feedback):
 *   The default profile targets ~500-900KB outputs. When the caller hits a
 *   network failure mid-upload, they can re-call with a more aggressive
 *   profile (e.g. {maxDim:720, quality:0.65}) to halve the file size and
 *   retry. See `postWithRetry` in DashboardSimulate.jsx for the adaptive
 *   ladder that keeps shrinking until SOMETHING fits through the proxy.
 *
 * Public API: compressImageForUpload(file, opts?) → Promise<File>
 */

const HEIC_RE = /\.(heic|heif)$/i;

function isHeicLike(file) {
  if (HEIC_RE.test(file.name || "")) return true;
  const t = (file.type || "").toLowerCase();
  return t === "image/heic" || t === "image/heif" || t.startsWith("image/heic");
}

/**
 * Convert a HEIC/HEIF file to JPEG client-side via heic2any.
 *
 * Why this exists (iter 92 — Android Xiaomi production feedback 18.06.2026):
 *   Xiaomi MIUI 13+ ships with "High-efficiency image format" enabled by
 *   default on many models — every camera shot is a 12-25 MB HEIC file.
 *   Prior to this iter we passed HEIC through `compressImageForUpload`
 *   unchanged because the browser canvas can't decode HEIC. The result:
 *   the original 12-25 MB body was sent on EVERY adaptive-retry attempt,
 *   tripping the production reverse-proxy size limit every single time and
 *   surfacing as a hard "Upload-ul a eșuat" toast with no path to recovery.
 *
 * Approach:
 *   - Lazy-import heic2any (~400 KB) so non-HEIC users don't pay the cost.
 *   - Convert to JPEG q92 (heic2any's own quality knob — the caller still
 *     gets to resize+recompress the result downstream).
 *   - On any failure (corrupt file, unsupported variant) we fall through to
 *     returning the original — the backend has `pillow_heif` registered and
 *     can still handle the raw bytes if the proxy lets them through.
 */
async function convertHeicToJpeg(file) {
  try {
    const mod = await import("heic2any");
    const heic2any = mod.default || mod;
    const blob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.92,
    });
    // heic2any can return Blob OR Blob[] (multi-image HEIC) — collapse to first frame.
    const out = Array.isArray(blob) ? blob[0] : blob;
    const baseName = (file.name || "upload").replace(/\.(heic|heif)$/i, "") || "upload";
    return new File([out], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("imageCompression: HEIC→JPEG conversion failed, keeping original", e);
    return null;
  }
}

async function decodeWithBitmap(file) {
  // createImageBitmap applies EXIF orientation when imageOrientation:'from-image'
  // is supported (Chromium 81+, Safari 14.5+). On older browsers we fall back
  // to <img> + canvas which loses orientation — we accept that trade-off.
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return await createImageBitmap(file).catch(() => null);
  }
}

async function decodeWithImageTag(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image decode failed"));
    };
    img.src = url;
  });
}

function targetSize(width, height, maxDim) {
  const longSide = Math.max(width, height);
  if (longSide <= maxDim) {
    return { width: Math.round(width), height: Math.round(height), resized: false };
  }
  const scale = maxDim / longSide;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    resized: true,
  };
}

// Default compression profile — picked to keep output under ~900KB for the
// typical 12MP phone photo while preserving enough detail for the AI pipeline
// to read door/window edges clearly. Mobile production feedback (09.06.2026)
// drove the previous 2048px / q85 setting down to 1280px / q80 so we comfortably
// fit under conservative reverse-proxy body limits (1-2MB).
const DEFAULTS = {
  maxDim: 1280,
  quality: 0.8,
  skipBelowBytes: 800 * 1024, // 800KB — anything smaller doesn't need work
};

export async function compressImageForUpload(file, opts = {}) {
  const { maxDim, quality, skipBelowBytes } = { ...DEFAULTS, ...opts };
  if (!file) return file;
  // HEIC/HEIF: try a client-side conversion to JPEG FIRST so the resize +
  // recompress pipeline below can still trim oversized phone photos. If the
  // conversion fails we hand off the original to the backend (pillow_heif)
  // — same fallback as before.
  let working = file;
  if (isHeicLike(file)) {
    const converted = await convertHeicToJpeg(file);
    if (!converted) return file;
    working = converted;
  }
  if (working.size < skipBelowBytes) return working;

  try {
    const source = (await decodeWithBitmap(working)) || (await decodeWithImageTag(working));
    if (!source) return working;
    const { width, height, resized } = targetSize(source.width, source.height, maxDim);

    // If we'd only be re-encoding without resizing AND the file is already
    // a smallish JPEG, skip — recompression would only add latency. Note
    // this gate is intentionally bypassed when the caller passes a tighter
    // `quality` (e.g. retry attempts) because they explicitly want a smaller
    // output.
    if (
      !resized &&
      (working.type || "").includes("jpeg") &&
      working.size < 2 * 1024 * 1024 &&
      quality >= DEFAULTS.quality
    ) {
      try {
        source.close?.();
      } catch (closeErr) {
        // eslint-disable-next-line no-console
        console.debug("imageCompression: source.close() unavailable", closeErr);
      }
      return working;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return working;
    ctx.drawImage(source, 0, 0, width, height);
    try {
      source.close?.();
    } catch (closeErr) {
      // eslint-disable-next-line no-console
      console.debug("imageCompression: source.close() unavailable", closeErr);
    }

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return working;

    // If the compressed result is somehow LARGER than the original, keep
    // the original (rare, happens on very dense PNG photos with random
    // noise). EXCEPT when the caller explicitly requested aggressive
    // compression (retry path) — in that case we want the smaller blob
    // even if it's only marginally smaller.
    if (blob.size >= working.size && quality >= DEFAULTS.quality) return working;

    const newName =
      (working.name || "upload.jpg").replace(/\.[a-zA-Z0-9]+$/, "") + ".jpg";
    return new File([blob], newName, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (e) {
    // Any failure → upload the (possibly HEIC-converted) working file.
    // eslint-disable-next-line no-console
    console.warn("imageCompression: pipeline failed, falling back to working file", e);
    return working;
  }
}
